import { useState, useCallback } from 'react';
import { getTasks, getTaskStats, createTask as apiCreateTask, updateTask as apiUpdateTask, deleteTask as apiDeleteTask, getAssessmentQuestions } from '../services/api';
import logger from '../utils/logger';

/**
 * Custom hook for managing tasks - handles CRUD operations and state management
 * @returns {Object} Task management state and functions
 */
const useTaskManagement = () => {
  const [rawTasks, setRawTasks] = useState([]); // Raw unfiltered tasks from API
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, has_next: false });
  const [assessmentQuestions, setAssessmentQuestions] = useState([]);

  /**
   * Fetch tasks from API with optional pagination and filters
   */
  const fetchTasks = useCallback(async (page = 1, append = false, loadAll = false, serverFilters = {}) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      // If loadAll is true, request all tasks (500 per page, which is the max)
      const perPage = loadAll ? 500 : 200;

      // Build API params with server-side filters
      const params = {
        page: page,
        per_page: perPage
      };

      // Add server-side filters if provided
      if (serverFilters.category && serverFilters.category !== 'all') {
        params.category = serverFilters.category;
      }
      if (serverFilters.questionId) {
        params.question_id = serverFilters.questionId;
      }

      const result = await getTasks(params);

      if (result.success && result.data.success) {
        if (append) {
          // Append new tasks to existing
          setRawTasks(prev => [...prev, ...result.data.tasks]);
        } else {
          // Replace tasks
          setRawTasks(result.data.tasks);
        }

        // Update pagination info
        if (result.data.pagination) {
          setPagination(result.data.pagination);

          // If loadAll is true and there are more pages, automatically fetch them
          if (loadAll && result.data.pagination.has_next) {
            // Recursively fetch next page with same filters
            await fetchTasks(page + 1, true, true, serverFilters);
          }
        }
      }
    } catch (error) {
      logger.error('Error fetching tasks', { error, page, loadAll, serverFilters });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  /**
   * Fetch task statistics
   */
  const fetchStats = useCallback(async () => {
    try {
      const result = await getTaskStats();
      if (result.success && result.data.success) {
        setStats(result.data.stats);
      }
    } catch (error) {
      logger.error('Error fetching task stats', { error });
    }
  }, []);

  /**
   * Fetch assessment questions for task creation
   */
  const fetchAssessmentQuestions = useCallback(async () => {
    try {
      const result = await getAssessmentQuestions();

      logger.debug('Assessment questions response received', { success: result.success, hasData: !!result.data });

      if (result.success && result.data) {
        // Handle both response formats
        if (result.data.questions) {
          logger.debug('Assessment questions loaded', { questionCount: result.data.questions.length });
          setAssessmentQuestions(result.data.questions);
        } else if (result.data.success && result.data.questions) {
          logger.debug('Assessment questions loaded', { questionCount: result.data.questions.length });
          setAssessmentQuestions(result.data.questions);
        }
      }
    } catch (error) {
      logger.error('Error fetching assessment questions', { error });
    }
  }, []);

  /**
   * Create a new task
   */
  const createTask = useCallback(async (taskData) => {
    try {
      // Validate required fields
      if (!taskData.title || !taskData.question_id) {
        throw new Error('Please fill in all required fields (Title and Question)');
      }

      const result = await apiCreateTask(taskData);

      // Check if task was created successfully
      // Backend returns { success: true, data: { success: true, message: ..., task: ... } }
      if (result.success && (result.data.success || result.data.task)) {
        return { success: true, data: result.data };
      }

      return { success: false, error: result.error || result.data?.error || 'Failed to create task' };
    } catch (error) {
      logger.error('Error creating task', { error, taskData });
      return { success: false, error: error.message || 'Failed to create task' };
    }
  }, []);

  /**
   * Update an existing task
   */
  const updateTask = useCallback(async (taskId, updates) => {
    try {
      await apiUpdateTask(taskId, updates);

      // Update the task in local state instead of refetching everything
      setRawTasks(prevTasks => prevTasks.map(task =>
        task.id === taskId ? { ...task, ...updates } : task
      ));

      return { success: true };
    } catch (error) {
      logger.error('Error updating task', { error, taskId, updates });
      return { success: false, error: error.message || 'Failed to update task' };
    }
  }, []);

  /**
   * Update task status
   */
  const updateTaskStatus = useCallback(async (taskId, newStatus, dueDate = null) => {
    try {
      const updateData = { status: newStatus };
      if (dueDate !== null) {
        updateData.due_date = dueDate;
      }
      return await updateTask(taskId, updateData);
    } catch (error) {
      logger.error('Error updating task status', { error, taskId, newStatus, dueDate });
      return { success: false, error: error.message };
    }
  }, [updateTask]);

  /**
   * Update task priority
   */
  const updateTaskPriority = useCallback(async (taskId, newPriority) => {
    try {
      return await updateTask(taskId, { priority: newPriority });
    } catch (error) {
      logger.error('Error updating task priority', { error, taskId, newPriority });
      return { success: false, error: error.message };
    }
  }, [updateTask]);

  /**
   * Delete a task
   */
  const deleteTask = useCallback(async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return { success: false, cancelled: true };
    }

    try {
      await apiDeleteTask(taskId);

      // Remove from local state
      setRawTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));

      return { success: true };
    } catch (error) {
      logger.error('Error deleting task', { error, taskId });
      return { success: false, error: error.message || 'Failed to delete task' };
    }
  }, []);

  return {
    // State
    rawTasks,
    stats,
    loading,
    loadingMore,
    pagination,
    assessmentQuestions,

    // Functions
    fetchTasks,
    fetchStats,
    fetchAssessmentQuestions,
    createTask,
    updateTask,
    updateTaskStatus,
    updateTaskPriority,
    deleteTask,

    // State setters (for direct manipulation if needed)
    setRawTasks,
    setStats,
  };
};

export default useTaskManagement;
